import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getActiveStoreContext, readCachedStoreContext } from './storeBootstrap.js';
import { getPlatformDomain, parseCustomDomainFromHost, parseSubdomainFromHost } from './storefrontHost.js';

export function isCustomDomainRoutingMode() {
    const ctx = getActiveStoreContext() || readCachedStoreContext();
    if (ctx?.routingMode === 'custom_domain') return true;
    if (typeof window !== 'undefined') {
        return Boolean(parseCustomDomainFromHost(window.location.hostname, getPlatformDomain()));
    }
    return false;
}

export function isSubdomainRoutingMode() {
    if (isCustomDomainRoutingMode()) return false;
    const ctx = getActiveStoreContext() || readCachedStoreContext();
    if (ctx?.routingMode === 'subdomain') return true;
    if (typeof window !== 'undefined') {
        return Boolean(parseSubdomainFromHost(window.location.hostname, getPlatformDomain()));
    }
    return false;
}

export function getStorePathPrefixFromPathname(pathname = '') {
    const match = String(pathname).match(/^\/s\/([^/]+)\/([^/]+)(?=\/|$)/i);
    if (match) return `/s/${match[1]}/${match[2]}`;
    if (isSubdomainRoutingMode() || isCustomDomainRoutingMode()) return '';
    return '';
}

export function withStorePathPrefix(pathname, path) {
    const prefix = getStorePathPrefixFromPathname(pathname);
    if (!prefix) return path;
    const clean = path.startsWith('/') ? path : `/${path}`;
    if (clean === '/') return prefix;
    return `${prefix}${clean}`;
}

export function useStorePathPrefix() {
    const { pathname } = useLocation();
    return useMemo(() => getStorePathPrefixFromPathname(pathname), [pathname]);
}

export function useStorePath() {
    const { pathname } = useLocation();
    return useCallback((path) => withStorePathPrefix(pathname, path), [pathname]);
}

function resolveAuthLanguage(pathname = '') {
    const match = String(pathname).match(/\/([a-z]{2})\/(?:sign-in|sign-up|profile)(?:\/|$)/i);
    return match?.[1] || 'en';
}

/** Return URLs for Google OAuth so the backend can redirect back to this storefront. */
export function getGoogleOAuthReturnInfo(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
    const prefix = getStorePathPrefixFromPathname(pathname);
    const lang = resolveAuthLanguage(pathname);
    const returnPath = prefix ? `${prefix}/auth/callback` : '/auth/callback';
    const signInPath = prefix ? `${prefix}/${lang}/sign-in` : `/${lang}/sign-in`;
    const signUpPath = prefix ? `${prefix}/${lang}/sign-up` : `/${lang}/sign-up`;

    return {
        returnOrigin: typeof window !== 'undefined' ? window.location.origin : '',
        returnPath,
        signInPath,
        signUpPath,
    };
}
